/**
 * Surface content — 3.9 Maintain · AMS, signals and the next upgrade.
 *
 * ZERO literals in the component (build brief §0a): every figure, name, id,
 * route and sentence this surface renders is declared here or read from
 * `@/demo/data`.
 *
 * CANONICAL TIME/STATE RESOLUTION (build brief §0). Van Dijk's phase is BUILD,
 * so it has no AMS history to show. This surface is therefore honest in two
 * registers, and says so on the page:
 *
 *  - VAN DIJK · THE PLAN — what Maintain will be for the hero project: AMS
 *    begins after hypercare, and the contract draft is attached to the
 *    blueprint. The ribbon reads `build` and the context pill reads
 *    MAINTAIN · PLAN.
 *  - BERGMAN WINKELS · THE LIVE EXEMPLAR — the seeded maintain-phase retail
 *    client (`projectById('bergman')`, AMS contract, €96k, live since 2025)
 *    carries the deck's operating figures: contract, ticket volume, the share
 *    of replies that start as an agent draft, and the signals table.
 *
 * The split follows the seed, not convenience: the deck's €96k/yr is exactly
 * `bergman.contractValue`, and the deck's "26 modules scanned" is exactly
 * `VAN_DIJK.build.modulesTotal`. So the upgrade-scout scan is Van Dijk's own
 * estate on the Odoo 19 → 20 hop (`vanDijk.odooVersion` → the next release),
 * and Bergman — which the seed puts on Odoo 18 — is never claimed to be on 19.
 * NEXT VERSION is a release fact about Odoo, not a claim about either client.
 */

import { NOW, VAN_DIJK, agentById, heroProject, personById, projectById, thinSpace } from '@/demo/data'
import type { PhaseKey } from '@/demo/data'
import { TRACEABILITY } from '@/demo/content/requirementsRegister'

const PROJECT = heroProject()
/** The live AMS client the plan is measured against. */
const EXEMPLAR = projectById('bergman')
const EXEMPLAR_LEAD = personById(EXEMPLAR.leadId)
const SCOUT = agentById('upgrade-scout')

const projectHref = (segment: string): string => `/project/${PROJECT.id}/${segment}`

/** Every affordance on this surface lands on one of these — no dead links. */
export const HREFS = {
  goLive: projectHref('go-live'),
  blueprint: projectHref('blueprint'),
  interviews: projectHref('interviews'),
  requirements: projectHref('requirements'),
  build: projectHref('build'),
  test: projectHref('test'),
  ams: projectHref('ams'),
  clients: '/clients',
  portal: '/portal',
  scoutConfig: `/library/agents/${SCOUT.id}`,
} as const

/** In-page targets, so "view run" and the run rows land on something real. */
export const ANCHORS = {
  exemplar: 'live-exemplar',
  signals: 'signals-next-tasks',
  scan: 'upgrade-scan',
  scanDetail: 'upgrade-scan-detail',
  trace: 'trace-chain',
} as const

/* ── A sentence that carries links, without literals in the component ───── */

export interface Fragment {
  text: string
  /** Present when this fragment is a link to a real route. */
  to?: string
}

/* ── Dates (measured against NOW — never Date.now()) ────────────────────── */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const

const dayLabel = (d: Date): string =>
  `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()] ?? ''} ${d.getUTCFullYear()}`

const addDays = (iso: string, days: number): Date =>
  new Date(new Date(`${iso}T00:00:00Z`).getTime() + days * 86_400_000)

/** Whole months from an ISO day to a moment, calendar-wise (no 30-day fudge). */
const monthsSince = (iso: string, to: Date): number => {
  const year = Number(iso.slice(0, 4))
  const month = Number(iso.slice(5, 7))
  const day = Number(iso.slice(8, 10))
  const months = (to.getFullYear() - year) * 12 + (to.getMonth() + 1 - month)
  return to.getDate() >= day ? months : months - 1
}

const HYPERCARE_WEEKS = 2
const AMS_START = addDays(PROJECT.goLive, HYPERCARE_WEEKS * 7)

/* ── Chrome ─────────────────────────────────────────────────────────────── */

export const MAINTAIN_PAGE = {
  moduleLabel: 'PX · Project',
  contextPill: 'MAINTAIN · PLAN',
  /** BUILD, not maintain — the overview and this page describe one world. */
  phase: 'build' as PhaseKey,
  title: 'Maintain — AMS, signals and the next upgrade',
  standfirst:
    'The project becomes an annuity: tickets feed signals, signals feed the next scope, and the upgrade scout ' +
    'arrives with the next version already quoted.',
} as const

/* ── 1 · Van Dijk: the plan ─────────────────────────────────────────────── */

export const PLAN = {
  label: `${PROJECT.client} · the plan`,
  sentence: [
    { text: 'AMS begins after hypercare (' },
    { text: `go-live ${VAN_DIJK.goLive.label}`, to: HREFS.goLive },
    { text: ` + two weeks). The contract draft is attached to the ` },
    { text: 'blueprint', to: HREFS.blueprint },
    { text: '.' },
  ] as readonly Fragment[],
  meta:
    `${PROJECT.client} is in ${PROJECT.phase}, ${VAN_DIJK.goLive.weeksAway} weeks from go-live — there is no ` +
    `ticket history yet. Hypercare runs ${HYPERCARE_WEEKS} weeks from ${VAN_DIJK.goLive.label}, so the AMS ` +
    `contract starts ${dayLabel(AMS_START)}. Everything below the plan is a live client, not a projection.`,
} as const

/* ── 2 · Bergman Winkels: the live exemplar ─────────────────────────────── */

const AMS_SINCE_YEAR = EXEMPLAR.goLive.slice(0, 4)
const MONTHS_LIVE = monthsSince(EXEMPLAR.goLive, NOW)

/** Deck figures, transcribed as given and attributed to the exemplar. */
const TICKETS = { last30: 41, monthOne: 118 } as const
const AGENT_DRAFT_PCT = 62
const RENEWS = 'Nov 2027'
const NEXT_VERSION = { number: 20, expected: 'Oct 2027' } as const

export interface MaintainKpi {
  key: string
  label: string
  figure: string
  qualifier: string
}

const euroThousands = (amount: number): string => `€${Math.round(amount / 1_000)}k`

export const EXEMPLAR_BLOCK = {
  label: `Live exemplar · ${EXEMPLAR.client} · AMS since ${AMS_SINCE_YEAR}`,
  heading: 'What Maintain looks like once it is running',
  meta:
    `Odoo ${EXEMPLAR.odooVersion} · ${EXEMPLAR.vertical} · ${EXEMPLAR.office} · ${EXEMPLAR.contractType} · ` +
    `lead ${EXEMPLAR_LEAD.name} · ${MONTHS_LIVE} months live`,
  clientsLinkLabel: 'the full AMS base',
} as const

export const EXEMPLAR_KPIS: readonly MaintainKpi[] = [
  {
    key: 'contract',
    label: 'AMS contract',
    figure: `${euroThousands(EXEMPLAR.contractValue)}/yr`,
    qualifier: `renews ${RENEWS}`,
  },
  {
    key: 'tickets',
    label: 'Tickets · 30 days',
    figure: thinSpace(TICKETS.last30),
    qualifier: `down from ${thinSpace(TICKETS.monthOne)} in month 1`,
  },
  {
    key: 'agent-draft',
    label: 'Resolved by agent draft',
    figure: `${AGENT_DRAFT_PCT}%`,
    qualifier: 'a human sends every reply',
  },
  {
    key: 'next-version',
    label: 'Next version',
    figure: `Odoo ${NEXT_VERSION.number}`,
    qualifier: `expected ${NEXT_VERSION.expected}`,
  },
]

/**
 * The trust model is a fact on this screen, not a footnote: the agent-draft
 * figure only means anything next to who presses send.
 */
export const TRUST = {
  pill: SCOUT.reviewGate,
  headline: `${AGENT_DRAFT_PCT}% of replies start as an agent draft. A human sends every one of them.`,
  detail:
    'No agent writes to a client system or sends a client a message. The draft is the work; the send is the ' +
    'decision, and it is recorded against a named person.',
} as const

/* ── 3 · Signals → next tasks (proposals — mint) ────────────────────────── */

export type EvidenceKind = 'Tickets' | 'Usage telemetry' | 'Performance log'

export interface Signal {
  key: string
  signal: string
  evidenceKind: EvidenceKind
  evidence: string
  proposed: string
  hours: number
}

export const SIGNALS: readonly Signal[] = [
  {
    key: 'promo-approvals',
    signal: 'Every promotion price change is approved one at a time',
    evidenceKind: 'Tickets',
    evidence: `14 of the last ${TICKETS.last30} tickets`,
    proposed: 'Batch approval screen',
    hours: 12,
  },
  {
    key: 'weekly-order',
    signal: 'Store managers export to a spreadsheet before the weekly order',
    evidenceKind: 'Usage telemetry',
    evidence: '61 exports a week, across 9 stores',
    proposed: 'Order proposal view',
    hours: 20,
  },
  {
    key: 'valuation-timeout',
    signal: 'Stock valuation times out in the last week of the month',
    evidenceKind: 'Performance log',
    evidence: '4 timeouts, all at month end',
    proposed: 'Report pre-aggregation',
    hours: 8,
  },
]

export const SIGNAL_HOURS = SIGNALS.reduce((sum, s) => sum + s.hours, 0)

export const SIGNALS_TABLE = {
  label: 'Signals → next tasks',
  heading: 'What the tickets are asking for next',
  proposalTag: 'Proposed by PX',
  columns: { signal: 'Signal', evidence: 'Evidence', proposed: 'Proposed' },
  caption: [
    { text: 'Signals become a quarterly improvement proposal, priced, for ' },
    { text: 'the client', to: HREFS.portal },
    { text: ' to approve.' },
  ] as readonly Fragment[],
  total: `${SIGNALS.length} signals · ${SIGNAL_HOURS} h proposed · nothing is scheduled until the client approves it.`,
  proposedLine: (signal: Signal): string => `${signal.proposed} · ${signal.hours} h`,
} as const

/* ── 4 · The upgrade scout (decided furniture + a proposal) ─────────────── */

const MODULES_SCANNED = VAN_DIJK.build.modulesTotal
const REWORK = { modules: 3, hours: 26 } as const
const RETIRE_MODULES = 2
const NO_CHANGE_MODULES = MODULES_SCANNED - REWORK.modules - RETIRE_MODULES
const DRAFT_QUOTE = 38_000
const DELIVERY_DAYS = 9

export interface ScanVerdict {
  key: string
  verdict: string
  modules: number
  meaning: string
}

export const SCAN_VERDICTS: readonly ScanVerdict[] = [
  {
    key: 'no-change',
    verdict: 'No change',
    modules: NO_CHANGE_MODULES,
    meaning: 'Standard configuration and library modules that carry across the version hop untouched.',
  },
  {
    key: 'rework',
    verdict: 'Rework',
    modules: REWORK.modules,
    meaning: `New-build modules whose hooks moved in the new release — ~${REWORK.hours} h between them.`,
  },
  {
    key: 'retire',
    verdict: 'Retire',
    modules: RETIRE_MODULES,
    meaning: 'Built for this client once, now standard in the product — the code is deleted, not migrated.',
  },
]

export const SCAN = {
  eyebrow: `${SCOUT.name} · Odoo ${PROJECT.odooVersion} → ${NEXT_VERSION.number}`,
  title: `${MODULES_SCANNED} modules scanned`,
  meta:
    `${NO_CHANGE_MODULES} no change · ${REWORK.modules} rework ~${REWORK.hours} h · ` +
    `${RETIRE_MODULES} retire (now standard)`,
  quoteChip: `Draft quote ${euroThousands(DRAFT_QUOTE)}`,
  daysChip: `${DELIVERY_DAYS} delivery days`,
  ctaLabel: 'Send to account manager',
  ctaHref: HREFS.clients,
  consequence:
    'Opens the quote on the client record — a person sends it, the scout never contacts the client.',
  scope: [
    { text: 'Scanned: the ' },
    { text: `${MODULES_SCANNED} modules on the build board`, to: HREFS.build },
    {
      text:
        `. ${SCOUT.name} · ${SCOUT.runs} — the next version is quoted before it ships, so a painful annual ` +
        `upgrade becomes a scheduled, pre-quoted event.`,
    },
  ] as readonly Fragment[],
} as const

export const SCAN_DETAIL = {
  label: 'Scan detail',
  heading: `Odoo ${PROJECT.odooVersion} → ${NEXT_VERSION.number} — verdict per module group`,
  columns: { verdict: 'Verdict', modules: 'Modules', meaning: 'What it means' },
  agentLine:
    `${SCOUT.name} · ${SCOUT.produces} · ${SCOUT.runs} · ${SCOUT.reviewGate} · autonomy ` +
    `${SCOUT.defaultAutonomy} · ${SCOUT.acceptancePct}% of its proposals accepted`,
  agentLinkLabel: 'agent configuration',
  agentHref: HREFS.scoutConfig,
} as const

/* ── The scout's run log ────────────────────────────────────────────────── */

export type RunKind = 'awaiting-send' | 'superseded'

export interface ScoutRun {
  key: string
  kind: RunKind
  label: string
  text: string
  time: string
  runHref: string
}

export const SCOUT_RUNS: readonly ScoutRun[] = [
  {
    key: 'scan-latest',
    kind: 'awaiting-send',
    label: SCOUT.reviewGate,
    text: `Odoo ${NEXT_VERSION.number} scan · ${MODULES_SCANNED} modules · draft quote ${euroThousands(DRAFT_QUOTE)} · ${DELIVERY_DAYS} delivery days`,
    time: '06:12',
    runHref: `#${ANCHORS.scanDetail}`,
  },
  {
    key: 'scan-previous',
    kind: 'superseded',
    label: 'Superseded',
    text: `Odoo ${NEXT_VERSION.number} scan · previous weekly pass · ${REWORK.modules + 1} modules flagged for rework`,
    time: '20 Aug',
    runHref: `#${ANCHORS.scanDetail}`,
  },
]

export const RUN_LOG = {
  label: 'Agent runs',
  heading: 'Every scan is logged, and every scan is reachable',
} as const

/* ── 5 · The trace line ─────────────────────────────────────────────────── */

/**
 * The chain is the register's own (`TRACEABILITY`) — imported, not retyped, so
 * the two surfaces can never drift on which requirement they are following.
 * The AMS stage is this page, so it renders as "you are here" rather than a
 * link back to itself.
 */
export const TRACE = {
  label: 'Trace',
  heading: TRACEABILITY.heading,
  lead: TRACEABILITY.lead,
  chain: TRACEABILITY.chain,
  hereHref: HREFS.ams,
  hereLabel: 'you are here',
  quote:
    'The context object that started in the first workshop is still the thing being read.',
  closing: TRACEABILITY.closing,
} as const

/* ── 6 · What this page withholds ───────────────────────────────────────── */

export const DENSITY_NOTE: readonly Fragment[] = [
  {
    text:
      `${PROJECT.client} shows the plan — it goes live ${VAN_DIJK.goLive.label} and has no AMS history yet. ` +
      `${EXEMPLAR.client} is the live exemplar, and its figures are its own. Ticket-level detail — all ` +
      `${TICKETS.last30} of the last 30 days — is withheld here; it lives on the AMS board in `,
  },
  { text: 'Clients & AMS', to: HREFS.clients },
  { text: ', which is also where the draft quote goes when a person sends it.' },
]
